import { useMatches } from '@tanstack/react-router'
import { useMemo } from 'react'
import { ehIdValido } from '@/components/state/NaoEncontrado'
import { usePatientName } from '@/features/pacientes/paciente'
import { useSessao } from '@/features/sessao/api'
import { useTimeZone } from '@/lib/tempo'

/*
 * A trilha do consultório.
 *
 * Cada rota declara a própria migalha em `staticData.crumb`, e a trilha sai
 * das rotas casadas com a URL. Ela nunca depende de onde a terapeuta veio, e
 * carregando, erro e sucesso mostram a mesma. Este arquivo é lido pela árvore
 * de rotas, que o portal também baixa: nada de componente pesado aqui.
 */

export interface Crumb {
  label: string
  /** Sem `to`, a migalha não vira link. A última nunca vira. */
  to?: string
}

export type NavCrumb =
  /** Rótulo fixo; o destino é o caminho da própria rota. */
  | { label: string; parents?: readonly Crumb[] }
  /**
   * Rótulo que depende de dado, como o nome do paciente. É hook: cada rota
   * chama sempre o mesmo, então a ordem dos hooks não muda.
   */
  | {
      useCrumbs: (params: Readonly<Record<string, string>>, pathname: string) => readonly Crumb[]
    }

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    crumb?: NavCrumb
  }
}

export interface TrailEntry {
  routeId: string
  crumb: NavCrumb
  params: Readonly<Record<string, string>>
  pathname: string
}

interface MatchLike {
  routeId: string
  pathname: string
  params: unknown
  staticData: { crumb?: NavCrumb }
}

function comoTexto(params: unknown): Readonly<Record<string, string>> {
  if (typeof params !== 'object' || params === null) return {}
  return Object.fromEntries(
    Object.entries(params).filter((par): par is [string, string] => typeof par[1] === 'string'),
  )
}

/** As rotas casadas que declaram migalha, da raiz para a folha. */
export function trailOf(matches: readonly MatchLike[]): TrailEntry[] {
  return matches.flatMap((match) =>
    match.staticData.crumb === undefined
      ? []
      : [
          {
            routeId: match.routeId,
            crumb: match.staticData.crumb,
            params: comoTexto(match.params),
            pathname: match.pathname,
          },
        ],
  )
}

export function useTrail(): TrailEntry[] {
  const matches = useMatches()
  return useMemo(() => trailOf(matches), [matches])
}

/* ------------------------------------------------- migalhas com dado -- */

export const PACIENTES: Crumb = { label: 'Pacientes e prontuários', to: '/app/pacientes' }

export function usePatientCrumbs(params: Readonly<Record<string, string>>): readonly Crumb[] {
  const id = params.id ?? ''
  const nome = usePatientName(ehIdValido(id) ? id : '')
  return [PACIENTES, { label: nome.data ?? 'Paciente', to: `/app/p/${id}` }]
}

const DIA = (tz: string) =>
  new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone: tz })

/**
 * A sessão mora em `/app/s`, fora de `/app/p` na URL, mas é de um paciente:
 * a trilha passa por ele. O paciente vem da linha da sessão.
 */
export function useSessionCrumbs(params: Readonly<Record<string, string>>): readonly Crumb[] {
  const id = params.id ?? ''
  const sessao = useSessao(ehIdValido(id) ? id : undefined)
  const patientId = sessao.data?.patientId ?? ''
  const nome = usePatientName(patientId)
  const tz = useTimeZone()
  const quando = sessao.data?.startsAt
  return [
    PACIENTES,
    ...(patientId === '' ? [] : [{ label: nome.data ?? 'Paciente', to: `/app/p/${patientId}` }]),
    {
      label: quando === undefined ? 'Sessão' : `Sessão ${DIA(tz).format(Date.parse(quando))}`,
      to: `/app/s/${id}/preparo`,
    },
  ]
}
